import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DashboardLayout } from '@/lib/widgets/types';
import { useAuth } from './use-auth';
import { supabase } from '@/integrations/supabase/client';

const STORAGE_KEY = 'dashboard-layout';

interface UseDashboardLayoutParams {
  layoutType: 'admin' | 'user';
  companyId?: string;
}

export function useDashboardLayout({ layoutType, companyId }: UseDashboardLayoutParams) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Generate old localStorage key for migration
  const getStorageKey = () => {
    const userId = user?.id || 'anonymous';
    const type = layoutType === 'admin' ? 'admin-dashboard' : `user-dashboard-${companyId}`;
    return `${STORAGE_KEY}-${userId}-${type}`;
  };

  // Fetch layout from database
  const { data: layout, isLoading } = useQuery({
    queryKey: ['dashboard-layout', user?.id, companyId, layoutType],
    queryFn: async () => {
      if (!user?.id || !companyId) return null;

      const { data, error } = await supabase
        .from('dashboard_layouts')
        .select('layout_data')
        .eq('user_id', user.id)
        .eq('company_id', companyId)
        .eq('layout_type', layoutType)
        .maybeSingle();

      if (error) {
        console.error('Error fetching dashboard layout:', error);
        return null;
      }

      if (data?.layout_data) {
        console.log('📦 Layout loaded from database:', data.layout_data);
        return data.layout_data as unknown as DashboardLayout;
      }

      // Try to migrate from localStorage
      try {
        const storageKey = getStorageKey();
        const stored = localStorage.getItem(storageKey);
        if (stored) {
          const parsedLayout = JSON.parse(stored) as DashboardLayout;
          console.log('📦 Migrating layout from localStorage:', parsedLayout);
          
          // Save to database
          const { error: saveError } = await supabase
            .from('dashboard_layouts')
            .insert({
              user_id: user.id,
              company_id: companyId,
              layout_type: layoutType,
              layout_data: parsedLayout as any
            });

          if (!saveError) {
            console.log('✅ Layout migrated to database successfully');
            // Clear localStorage after successful migration
            localStorage.removeItem(storageKey);
          }

          return parsedLayout;
        }
      } catch (migrationError) {
        console.error('Error migrating from localStorage:', migrationError);
      }

      return null;
    },
    enabled: !!user?.id && !!companyId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Save layout mutation
  const saveMutation = useMutation({
    mutationFn: async (newLayout: DashboardLayout) => {
      if (!user?.id || !companyId) {
        throw new Error('User or company not found');
      }

      const { error } = await supabase
        .from('dashboard_layouts')
        .upsert({
          user_id: user.id,
          company_id: companyId,
          layout_type: layoutType,
          layout_data: newLayout as any
        }, {
          onConflict: 'user_id,company_id,layout_type'
        });

      if (error) throw error;
      
      console.log('✅ Layout saved to database:', newLayout);
      return newLayout;
    },
    onSuccess: (newLayout) => {
      queryClient.setQueryData(
        ['dashboard-layout', user?.id, companyId, layoutType],
        newLayout
      );
    },
  });

  // Reset layout mutation
  const resetMutation = useMutation({
    mutationFn: async () => {
      if (!user?.id || !companyId) {
        throw new Error('User or company not found');
      }

      const { error } = await supabase
        .from('dashboard_layouts')
        .delete()
        .eq('user_id', user.id)
        .eq('company_id', companyId)
        .eq('layout_type', layoutType);

      if (error) throw error;
      
      console.log('✅ Layout reset in database');
    },
    onSuccess: () => {
      queryClient.setQueryData(
        ['dashboard-layout', user?.id, companyId, layoutType],
        null
      );
    },
  });

  const saveLayout = (newLayout: DashboardLayout) => {
    return saveMutation.mutateAsync(newLayout);
  };

  const resetLayout = () => {
    return resetMutation.mutateAsync();
  };

  return {
    layout: layout || null,
    saveLayout,
    resetLayout,
    isLoading
  };
}